import * as React from 'react';
import { createPortal } from 'react-dom';
import { useReducedMotion } from 'framer-motion';
import { cn } from '../lib/cn';

/**
 * AlertDialog — the confirm step for destructive or irreversible actions.
 *
 * docs/06 §4.1 requires one for: approving or rejecting a producer, blocking a user,
 * cancelling an order, and publishing a negative margin. Every one of those is a thing Merdan
 * cannot take back, and several of them send an email to a stranger the moment he clicks.
 *
 * The dialog must NAME THE TARGET ("Отклонить производителя «Josh»?"). A generic "Are you
 * sure?" trains people to click Yes without reading, which is worse than no confirmation at
 * all — it adds a click and removes the thinking.
 *
 * Implemented on the native <dialog> element rather than pulling in Radix: it gives us the
 * focus trap, Esc-to-close, and the backdrop for free, and every third-party dependency is a
 * dependency that has to be audited on a platform like this one.
 */

type AlertDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
};

const AlertDialogContext = React.createContext<{ onOpenChange: (open: boolean) => void }>({
  onOpenChange: () => {},
});

/*
 * Close animation: `dialog.close()` yanks the element to display:none the same frame, so the exit
 * has to run BEFORE close is called. Dropping `open` flips a `closing` state that applies the
 * `-out` keyframes (dialog panel and ::backdrop both), and only after they have run does the
 * timeout actually close the element. The open animation needs no JS — showModal() flips
 * display:none → block, which restarts the `-in` keyframes on the element every time.
 * Under `prefers-reduced-motion` the keyframes are off (`motion-safe:`) and the JS closes
 * immediately rather than holding a frozen dialog for the exit duration.
 */
const ALERT_EXIT_MS = 150; // ≥ the 140ms `dialog-out` keyframes, so `forwards` covers the gap.

export function AlertDialog({ open, onOpenChange, children }: AlertDialogProps) {
  const ref = React.useRef<HTMLDialogElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const [closing, setClosing] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (open) {
      setClosing(false);
      // showModal (not show) is what gives us the focus trap and the inert background. A reopen
      // that lands mid-exit finds the element still open and simply cancels the exit.
      if (!el.open) el.showModal();
      return;
    }
    if (!el.open) return;

    if (shouldReduceMotion) {
      el.close();
      return;
    }

    setClosing(true);
    const timer = window.setTimeout(() => {
      setClosing(false);
      el.close();
    }, ALERT_EXIT_MS);
    return () => {
      window.clearTimeout(timer);
      setClosing(false);
    };
  }, [open, shouldReduceMotion]);

  /*
   * PORTALLED to <body>.
   *
   * Confirmations are rendered next to the thing they confirm — and in this app that thing is
   * usually a table row, so the <dialog> was being emitted inside <tbody>. React rightly warned
   * ("<dialog> cannot appear as a child of <tbody>"): a table may only contain table content, and
   * a browser is free to hoist stray elements out of it, which would silently break the dialog.
   *
   * A modal belongs at the top of the document anyway. The portal also lifts it out of the table
   * card's `overflow: hidden`, which would otherwise clip it.
   */
  const dialog = (
    <dialog
      ref={ref}
      // Esc fires `cancel`; keep React state in sync or the dialog will refuse to reopen.
      onCancel={(e) => {
        e.preventDefault();
        onOpenChange(false);
      }}
      onClose={() => onOpenChange(false)}
      // Tokens, not palette. The scrim is the theme's own ink at 40% (`foreground/40`) rather than
      // a cold slate, and the panel is a `card` on the warm-stone surface the apps actually use.
      // No `dark:` variants: theming here is CSS variables, and the variables already swap.
      // The `-out` pair is keyed off [data-closing] and wins over the always-present `-in` pair
      // on specificity (class + attribute). Keyframes live in the shared tailwind preset and the
      // admin's config — both define the same `dialog-in/out` + `backdrop-in/out` vocabulary.
      data-closing={closing ? '' : undefined}
      className={cn(
        'backdrop:bg-foreground/40 backdrop:backdrop-blur-sm',
        'w-full max-w-md rounded-xl border border-border bg-card p-0 text-card-foreground shadow-xl',
        'motion-safe:animate-dialog-in motion-safe:backdrop:animate-backdrop-in',
        'motion-safe:data-[closing]:animate-dialog-out motion-safe:data-[closing]:backdrop:animate-backdrop-out',
      )}
    >
      {/* Children stay mounted through the exit — an empty panel fading out reads as a glitch. */}
      {(open || closing) && <div className="p-6">{children}</div>}
    </dialog>
  );

  return (
    <AlertDialogContext.Provider value={{ onOpenChange }}>
      {typeof document === 'undefined' ? dialog : createPortal(dialog, document.body)}
    </AlertDialogContext.Provider>
  );
}

export function AlertDialogContent({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('space-y-4', className)} {...props} />;
}

export function AlertDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('space-y-1.5', className)} {...props} />;
}

export function AlertDialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-lg font-semibold tracking-tight', className)} {...props} />;
}

export function AlertDialogDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn('text-sm leading-relaxed text-muted-foreground', className)}
      {...props}
    />
  );
}

export function AlertDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex justify-end gap-2 pt-2', className)} {...props} />
  );
}

/**
 * The destructive action. Styled `destructive` so it is never mistaken for the safe one — and
 * paired with `destructive-foreground` rather than a bare `text-white`, so the label follows the
 * token if a theme ever darkens the red.
 */
export function AlertDialogAction({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'rounded-xl bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground transition',
        'hover:bg-destructive/90 disabled:opacity-60',
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogCancel({
  className,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { onOpenChange } = React.useContext(AlertDialogContext);

  return (
    <button
      type="button"
      onClick={(e) => {
        onClick?.(e);
        onOpenChange(false);
      }}
      // The same border/hover recipe as Button's `outline` variant (`border-input`, `hover:bg-muted`),
      // so the safe choice next to the red one is the button the rest of the app already uses.
      className={cn(
        'rounded-xl border border-input px-4 py-2 text-sm font-medium transition',
        'hover:bg-muted',
        className,
      )}
      {...props}
    />
  );
}
